import { _ } from "extensions/underscore";

import { store } from "common/store";
import { weekToText } from "common/utils";
import { toHex } from "common/colors";

import Highcharts from "highcharts/es-modules/masters/highcharts.src";
import Highmaps from "highcharts/es-modules/masters/highmaps.src";

import "highcharts/es-modules/masters/modules/exporting.src";
import "./highcharts.scss";

Highcharts.setOptions({
    exporting: {
        enabled: false
    },
    credits: {
        enabled: false
    },
    title: {
        text: ""
    },
    colors: [
        toHex("wcag-blue"), // 0
        toHex("wcag-yellow"), // 1
        toHex("wcag-teal"), // 2
        toHex("wcag-magenta"), // 3
        toHex("wcag-dark-purple"), // 4
        toHex("wcag-red"), // 5
        toHex("paz-black-l-30"), // 6 series beyond this threshold are insignificant enough to lump together
        toHex("paz-black-l-30"), // 7
        toHex("paz-black-l-30"), // 8
        toHex("paz-black-l-30"), // 9
        toHex("paz-black-l-30") // 10
    ],
    lang: {
        drillUpText: "Back to all",
        thousandsSep: ",",
        numericSymbols: ["K", "M", "B", "T", "P", "E"]
    }
});

/**
 * Overriding the formatter while my issue is not released yet:
 * https://github.com/highslide-software/highcharts.com/issues/3944
 */
const originalPointFormatter = Highcharts.Point.prototype.tooltipFormatter;
Highcharts.Point.prototype.tooltipFormatter = function(pointFormat) {
    this.series.tooltipOptions.valuePrefix = _.result(this.series.tooltipOptions, "valuePrefix");
    this.series.tooltipOptions.valueSuffix = _.result(this.series.tooltipOptions, "valueSuffix");
    return originalPointFormatter.apply(this, arguments);
};

const originalGetStyle = Highcharts.getStyle;
Highcharts.getStyle = function(...args) {
    try {
        return originalGetStyle.apply(this, args);
    } catch (error) {
        // https://github.com/highcharts/highcharts/issues/13929
        // https://sentry.io/organizations/pazien/issues/1333362847/?cursor=0%3A300%3A0&project=149293&sort=freq&statsPeriod=14d
        if (error.message === "Unspecified error") return 0;
        // re-throw the error if it's not related to running getBoundingClientRect on a disconnected node in IE11
        throw error;
    }
};

Highcharts.pazUtils = {
    /**
     * Create a gradient from base color to transparent
     * @param base
     * @returns {{linearGradient: number[], stops: *[]}}
     */
    gradient(base) {
        return {
            linearGradient: [0, 0, 0, 300],
            stops: [
                [0, base],
                [1, "rgba(255,255,255, 0)"]
            ]
        };
    }
};

/**
 * Add custom date formats
 */
Highcharts.dateFormats = {
    G: weekToText
};

Highcharts.contains = function(parentNode) {
    if (!parentNode || !Highcharts.charts || !Highcharts.charts.length) { return []; }
    
    return Highcharts.charts.filter((chart) => chart && parentNode.contains(chart.container) && chart.container.offsetWidth);
};

/**
 * Add method to reflow all charts in a given node
 */
Highcharts.reflowAll = function(parentNode = document.querySelector("body")) {
    Highcharts.contains(parentNode).forEach((chart) => chart.reflow());
};

Highcharts.Renderer.prototype.symbols.vline = function(x, y, width, height) {
    return ["M", x, y + width / 2, "L", x + height, y + width / 2];
};
Highcharts.Renderer.prototype.symbols.hline = function(x, y, width, height) {
    return ["M", x, y + height / 2, "L", x + width, y + width / 2];
};

Highcharts.Chart.prototype.exportPazienChart = function({ format = "png", filename = "chart" } = {}, chartOptions = {}) {
    const BRANDING_LOGO = {
        url: "https://cdn.pazien.com/assets/powered-by-pazien@2x.png",
        height: 15,
        width: 95,
        padding: 10
    };
    
    const formats = {
        png: "image/png",
        jpeg: "image/jpeg",
        pdf: "application/pdf",
        svg: "image/svg+xml"
    };
    
    const state = store.getState();
    this.exportChart({
        filename,
        type: formats[format],
        url: state.config.highcharts_export,
        sourceWidth: 960
    }, {
        ...chartOptions,
        chart: {
            events: {
                load() {
                    const x = this.chartWidth - BRANDING_LOGO.width - BRANDING_LOGO.padding;
                    const y = this.chartHeight - BRANDING_LOGO.height - BRANDING_LOGO.padding;
                    
                    this.renderer
                        .image(BRANDING_LOGO.url, x, y, BRANDING_LOGO.width, BRANDING_LOGO.height)
                        .add();
                }
            },
            spacingBottom: BRANDING_LOGO.height + 15,
            ...chartOptions.chart
        }
    });
};

export { Highcharts, Highmaps };
